import { ApiResponse, PaginatedApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import {
  BerthResponse,
  BerthListQueryParams,
  FeaturedBerthResponse,
  MediaPayload,
  IBerthData,
  ByIdResponse,
  CreateBerthGroupPayload,
  Mooring,
  UnavailabilityResponse,
  ExternalReservationResponse,
  CreateExternalReservationPayload,
  UpdateExternalReservationPayload,
  ReleaseExternalReservationPayload,
} from "@/types/berth-management.types";
import { UpdateFeaturedPayload } from "@/types/harbor-management.types";
import { PublicListValue } from "@/types/list-value.types";

export const BerthManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBerths: builder.query<
      PaginatedApiResponse<BerthResponse[]>,
      BerthListQueryParams
    >({
      query: (params) => ({
        url: "berths",
        method: "GET",
        params,
      }),
      transformResponse: (response: PaginatedApiResponse<BerthResponse[]>) =>
        response,
      providesTags: ["Berths"],
      keepUnusedDataFor: 0,
    }),

    getBerthById: builder.query<ByIdResponse, string>({
      query: (berth_id) => ({
        url: `berths/${berth_id}`,
        method: "GET",
      }),
      transformResponse: (response: ByIdResponse) => response,
      keepUnusedDataFor: 0,
    }),

    createBerth: builder.mutation<
      BerthResponse,
      Partial<IBerthData> | FormData
    >({
      query: (data) => ({
        url: "berths",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["Berths"],
    }),

    updateBerth: builder.mutation<
      ApiResponse<BerthResponse>,
      { berth_id: string; data: Partial<BerthResponse> | FormData }
    >({
      query: ({ berth_id, data }) => ({
        url: `berths/${berth_id}`,
        method: "PATCH",
        data,
      }),
      transformResponse: (response: ApiResponse<BerthResponse>) => response,
      invalidatesTags: ["Berths"],
    }),

    deleteBerth: builder.mutation<ApiResponse<void>, string>({
      query: (berth_id) => ({
        url: `berths/${berth_id}`,
        method: "DELETE",
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Berths"],
    }),

    getFeaturedBerths: builder.query<
      PaginatedApiResponse<FeaturedBerthResponse[]>,
      Record<string, never>
    >({
      query: () => ({
        url: "/featured-listings/berth/featured",
        method: "GET",
      }),
      transformResponse: (
        response: PaginatedApiResponse<FeaturedBerthResponse[]>,
      ) => response,
      providesTags: ["FeaturedBerths"],
      keepUnusedDataFor: 0,
    }),

    updateFeaturedBerth: builder.mutation<
      ApiResponse<void>,
      UpdateFeaturedPayload
    >({
      query: ({ harborId, featured }) => ({
        url: "/featured-listings/berth/featured",
        method: "PATCH",
        data: { harborId, featured },
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["FeaturedBerths", "Berths"],
    }),

    createMediaBerthId: builder.mutation<void, MediaPayload>({
      query: ({ id, media }) => {
        const formData = new FormData();
        media.forEach((file) => {
          formData.append(`media[]`, file);
        });
        return {
          url: `/berths/${id}/media`,
          method: "POST",
          data: formData,
        };
      },
      invalidatesTags: ["Berths"],
    }),

    createBerthGroup: builder.mutation<
      ApiResponse<void>,
      CreateBerthGroupPayload
    >({
      query: (data) => ({
        url: "berth-pier",
        method: "POST",
        data,
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Berths", "BerthGroups"],
    }),

    getMoorings: builder.query<Mooring[], void>({
      query: () => ({
        url: "/list-values/mooring-style/public",
        method: "GET",
      }),
      transformResponse: (response: PublicListValue[]) =>
        response.map((item) => ({
          id: item.id,
          name: item.key,
          description: item.description || "",
          label: item.label || item.name || item.key,
        })),
    }),

    blockUnavailabilityBerth: builder.mutation<
      UnavailabilityResponse,
      {
        id: string;
        startDate?: string;
        endDate?: string;
        reason: string;
      }
    >({
      query: ({ id, startDate, endDate, reason }) => {
        const data: { reason: string; startDate?: string; endDate?: string } = {
          reason,
        };

        // Only include startDate and endDate if they are provided
        if (startDate) {
          data.startDate = startDate;
        }
        if (endDate) {
          data.endDate = endDate;
        }

        return {
          url: `berths/${id}/block-unavailability`,
          method: "PATCH",
          data,
        };
      },
      transformResponse: (response: UnavailabilityResponse) => response,
      invalidatesTags: ["Berths", "Bookings"],
    }),

    unblockUnavailabilityBerth: builder.mutation<
      UnavailabilityResponse,
      {
        id: string;
        unavailabilityId: string;
      }
    >({
      query: ({ id, unavailabilityId }) => ({
        url: `berths/${id}/unblock-unavailability`,
        method: "PATCH",
        data: { unavailabilityId },
      }),
      transformResponse: (response: UnavailabilityResponse) => response,
      invalidatesTags: ["Berths", "Bookings"],
    }),

    createExternalReservation: builder.mutation<
      ExternalReservationResponse,
      CreateExternalReservationPayload
    >({
      query: ({ id, startDate, endDate, reason, externalReference }) => {
        const data: {
          reason: string;
          startDate?: string;
          endDate?: string;
          externalReference?: string;
        } = { reason };

        if (startDate) {
          data.startDate = startDate;
        }
        if (endDate) {
          data.endDate = endDate;
        }
        if (externalReference) {
          data.externalReference = externalReference;
        }

        return {
          url: `berths/${id}/external-reservation`,
          method: "PATCH",
          data,
        };
      },
      transformResponse: (response: ExternalReservationResponse) => response,
      invalidatesTags: ["Berths", "Bookings"],
    }),

    updateExternalReservation: builder.mutation<
      { updated: boolean },
      UpdateExternalReservationPayload
    >({
      query: ({ id, unavailabilityId, ...rest }) => ({
        url: `berths/${id}/external-reservation/${unavailabilityId}`,
        method: "PATCH",
        data: rest,
      }),
      invalidatesTags: ["Berths", "Bookings"],
    }),

    releaseExternalReservation: builder.mutation<
      { released: boolean },
      ReleaseExternalReservationPayload
    >({
      query: ({ id, unavailabilityId }) => ({
        url: `berths/${id}/release-external-reservation`,
        method: "PATCH",
        data: { unavailabilityId },
      }),
      invalidatesTags: ["Berths", "Bookings"],
    }),

    downloadBerthQrCode: builder.mutation<Blob, string>({
      query: (id) => ({
        url: `berths/${id}/qr-code`,
        method: "GET",
        responseType: "blob",
      }),
      transformResponse: (response: unknown): Blob => response as Blob,
    }),
  }),
});

export const {
  useGetBerthsQuery,
  useGetBerthByIdQuery,
  useCreateBerthMutation,
  useUpdateBerthMutation,
  useDeleteBerthMutation,
  useGetFeaturedBerthsQuery,
  useUpdateFeaturedBerthMutation,
  useCreateMediaBerthIdMutation,
  useCreateBerthGroupMutation,
  useGetMooringsQuery,
  useBlockUnavailabilityBerthMutation,
  useUnblockUnavailabilityBerthMutation,
  useCreateExternalReservationMutation,
  useUpdateExternalReservationMutation,
  useReleaseExternalReservationMutation,
  useDownloadBerthQrCodeMutation,
} = BerthManagementService;
